/**
 * VoiceMessage — WhatsApp-style audio playback with waveform visualization
 *
 * Three-section layout: Play button + Waveform + Metadata row.
 * Designed to be the "bubble owner" — MessageBubble applies themed
 * colors (primary-50 sent / white received) and this component controls
 * the interior layout including timestamp & delivery ticks.
 *
 * Features:
 * - 40-bar static waveform that fills with color as audio plays
 * - Framer Motion micro-interaction on play/pause button
 * - Smooth CSS-transitioned progress on waveform bars
 * - Seekable waveform (click to jump)
 * - Internal metadata row: duration + timestamp + delivery status
 * - Real waveform peaks from recording, pseudo-random fallback
 */

import { useState, useRef, useCallback, useEffect, memo } from 'react';
import { motion } from 'motion/react';
import { chatHaptic } from '../utils/chatHaptic';
import { MessageStatus } from './MessageStatus';
import type { MessageDeliveryStatus } from './MessageStatus';
import { ChatAvatar } from './ChatAvatar';
import { MSG_COLORS } from '@/constants/theme';

interface VoiceMessageProps {
  audioUrl: string;
  duration?: number;
  isMine?: boolean;
  className?: string;
  waveformPeaks?: number[];
  timestamp?: string;
  deliveryStatus?: MessageDeliveryStatus;
  senderAvatar?: string | null;
  senderName?: string;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const BAR_COUNT = 40;

export const VoiceMessage = memo(function VoiceMessage({
  audioUrl,
  duration = 0,
  isMine = false,
  className = '',
  waveformPeaks,
  timestamp,
  deliveryStatus,
  senderAvatar,
  senderName,
}: VoiceMessageProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration && isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
      }
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration && isFinite(audio.duration)) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    });

    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [audioUrl]);

  const togglePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const audio = audioRef.current;
      if (!audio) return;

      chatHaptic.voiceTap();
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    },
    [isPlaying],
  );

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * audio.duration;
  }, []);

  // Resample peaks to BAR_COUNT bars, or generate pseudo-waveform
  const waveform =
    waveformPeaks && waveformPeaks.length > 0
      ? Array.from({ length: BAR_COUNT }, (_, i) => {
          const idx = Math.floor((i / BAR_COUNT) * waveformPeaks.length);
          return Math.max(0.12, Math.min(1, waveformPeaks[idx] ?? 0.2));
        })
      : Array.from({ length: BAR_COUNT }, (_, i) => {
          const base = Math.sin(i * 0.8) * 0.4 + 0.5;
          const jitter = Math.sin(i * 2.3 + 1.7) * 0.2;
          return Math.max(0.12, Math.min(1, base + jitter));
        });

  const activeBarIndex = Math.floor((progress / 100) * BAR_COUNT);

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {/* Sender avatar with mic badge */}
      <div className="relative flex-shrink-0">
        <ChatAvatar
          src={senderAvatar}
          name={senderName || '?'}
          size="h-10 w-10"
          textSize="text-xs"
          borderColor="border-transparent"
          className="shadow-sm"
        />
        <div className="absolute -bottom-0.5 -right-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-slate-700 shadow-sm">
          <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            <path d="M19 10v2a7 7 0 01-14 0v-2" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {/* Play/Pause button with micro-interaction */}
      <motion.button
        type="button"
        onClick={togglePlay}
        whileTap={{ scale: 0.9 }}
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-700 text-white shadow-sm"
      >
        {isPlaying ? (
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg className="h-3.5 w-3.5 ms-0.5" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="6,3 20,12 6,21" />
          </svg>
        )}
      </motion.button>

      {/* Waveform + metadata */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Waveform bars */}
        <div
          className="flex h-8 cursor-pointer items-center gap-[2px]"
          onClick={handleSeek}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          tabIndex={0}
        >
          {waveform.map((height, i) => {
            const isActive = i < activeBarIndex;
            const isEdge = isPlaying && isActive && i > activeBarIndex - 3;
            return (
              <div
                key={i}
                className="flex-1 rounded-full"
                style={{
                  height: `${height * 100}%`,
                  backgroundColor: isActive
                    ? 'var(--color-neutral-700)'
                    : isMine
                      ? MSG_COLORS.sentWaveform
                      : MSG_COLORS.receivedWaveform,
                  transform: isEdge ? 'scaleY(1.15)' : 'scaleY(1)',
                  transition: 'background-color 150ms ease, transform 200ms ease',
                }}
              />
            );
          })}
        </div>

        {/* Metadata row: duration + timestamp + delivery status */}
        <div className="mt-0.5 flex items-center justify-between">
          <span
            className="text-[10px] font-medium tabular-nums"
            style={{ color: isMine ? MSG_COLORS.sentMetaText : MSG_COLORS.receivedMetaText }}
          >
            {isPlaying || currentTime > 0
              ? formatDuration(currentTime)
              : formatDuration(totalDuration)}
          </span>
          <div className="flex items-center gap-1">
            {timestamp && (
              <span
                className="text-[10px] tabular-nums"
                style={{ color: isMine ? MSG_COLORS.sentMetaText : MSG_COLORS.receivedMetaText, opacity: isMine ? 0.7 : 1 }}
              >
                {timestamp}
              </span>
            )}
            {isMine && deliveryStatus && (
              <MessageStatus status={deliveryStatus} className="h-3.5 w-3.5" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default VoiceMessage;
